import { defineCustomElement } from 'vue';

import {pcbHello,pcbForm,pcbFormPlus,pcbItem,pcbCart} from "@jp/pcb"
import "./style.css"
import * as utils from "./utils.js"

// 引入主题样式文件
const componentList = [
    pcbHello,
    pcbForm,
    pcbFormPlus,
    pcbItem,
    pcbCart
]

// 全局配置对象，允许用户自定义CSS路径
window.V3ComponentsConfig = window.V3ComponentsConfig || {
    cssPath: null, // 用户可以设置自定义CSS路径
    enableStyleInjection: true, // 是否启用样式注入
    debug: true, // 是否开启调试模式
    preloadStyles: true // 是否预加载样式以避免闪烁
};

// 全局CSS缓存
let globalCSSContent = null;
let cssLoadPromise = null;
// 获取当前脚本的路径来推断CSS文件位置
function getCSSPath() {
    const config = window.V3ComponentsConfig;
    
    // 优先使用用户配置的路径
    if (config.cssPath) {
        if (config.debug) console.log('使用配置的CSS路径:', config.cssPath);
        return config.cssPath;
    }
    
    // 方法1: 尝试从当前脚本标签获取路径
    const scripts = document.querySelectorAll('script[src]');
    for (let script of scripts) {
        if (script.src.includes('v3-components.iife.js')) {
            const cssPath = script.src.replace('v3-components.iife.js', 'v3-components.css');
            if (config.debug) console.log('从脚本标签推断CSS路径:', cssPath);
            return cssPath;
        }
    }
    
    // 方法2: 尝试从document.currentScript获取当前脚本路径
    if (document.currentScript && document.currentScript.src) {
        const cssPath = document.currentScript.src.replace('v3-components.iife.js', 'v3-components.css');
        if (config.debug) console.log('从document.currentScript推断CSS路径:', cssPath);
        return cssPath;
    }
    
    // 方法3: 检查是否已经有CSS文件被加载
    const existingCSS = document.querySelector('link[href*="v3-components.css"]');
    if (existingCSS) {
        if (config.debug) console.log('使用已加载的CSS路径:', existingCSS.href);
        return existingCSS.href;
    }
    
    // 方法4: 基于当前文档位置的fallback
    const currentPath = window.location.href.split('/').slice(0, -1).join('/');
    const fallbackPath = `${currentPath}/v3-components.css`;
    if (config.debug) console.log('使用fallback CSS路径:', fallbackPath);
    return fallbackPath;
}

// 获取CSS内容的函数
async function getCSSContent() {
    // 如果已经缓存了CSS内容，直接返回
    if (globalCSSContent) {
        return globalCSSContent;
    }
    
    // 如果正在加载中，等待加载完成
    if (cssLoadPromise) {
        return await cssLoadPromise;
    }
    
    // 开始加载CSS
    cssLoadPromise = loadCSSContent();
    globalCSSContent = await cssLoadPromise;
    return globalCSSContent;
}

async function loadCSSContent() {
    // 首先尝试从已加载的样式表获取
    for (let sheet of document.styleSheets) {
        try {
            if (sheet.href && sheet.href.includes('v3-components.css')) {
                const rules = Array.from(sheet.cssRules || sheet.rules || []);
                return rules.map(rule => rule.cssText).join('\n');
            }
        } catch (e) {
            // 跨域访问可能失败，继续尝试其他方法
            continue;
        }
    }
    
    // 如果无法从样式表获取，尝试fetch CSS文件
    try {
        const cssPath = getCSSPath();
        const response = await fetch(cssPath);
        if (response.ok) {
            return await response.text();
        }
    } catch (e) {
        console.warn('Unable to fetch CSS content:', e);
    }
    
    return '';
}

// 创建自定义元素时手动注入样式
componentList.forEach(component => {
    const MyCustomElement = defineCustomElement(component);
    
    // 重写connectedCallback来注入样式
    const originalConnectedCallback = MyCustomElement.prototype.connectedCallback;
    MyCustomElement.prototype.connectedCallback = async function() {
        const config = window.V3ComponentsConfig;
        
        // 防闪烁：在样式加载前先隐藏组件
        if (config.preloadStyles && this.shadowRoot) {
            this.style.visibility = 'hidden';
            this.style.opacity = '0';
        }
        
        if (originalConnectedCallback) {
            originalConnectedCallback.call(this);
        }
        
        // 注入样式到Shadow DOM
        if (config.enableStyleInjection && this.shadowRoot ) {
            try {
                // 首先尝试内联样式方式
                const cssContent = await getCSSContent();
                if (cssContent) {
                    const style = document.createElement('style');
                    style.id = 'arco-styles';
                    style.textContent = cssContent;
                    this.shadowRoot.appendChild(style);
                } else {
                    // fallback到链接方式
                    const link = document.createElement('link');
                    link.id = 'arco-styles';
                    link.rel = 'stylesheet';
                    link.href = getCSSPath();
                    this.shadowRoot.appendChild(link);
                }
                
                // 样式注入完成后，显示组件并添加淡入动画
                if (config.preloadStyles) {
                    // 使用requestAnimationFrame确保样式已经应用
                    requestAnimationFrame(() => {
                        this.style.visibility = 'visible';
                        this.style.opacity = '1';
                        this.style.transition = 'opacity 0.15s ease-in-out';
                    });
                }
            } catch (error) {
                if (config.debug) console.error('样式注入失败:', error);
                // 即使样式注入失败，也要显示组件
                if (config.preloadStyles) {
                    this.style.visibility = 'visible';
                    this.style.opacity = '1';
                }
            }
        } else if (config.preloadStyles) {
            // 如果没有启用样式注入，直接显示组件
            this.style.visibility = 'visible';
            this.style.opacity = '1';
        }
    };
    
    // 注册自定义元素
    customElements.define(component.name, MyCustomElement);
});

// 预加载CSS以避免闪烁
(async function preloadCSS() {
    const config = window.V3ComponentsConfig;
    if (config.preloadStyles && config.enableStyleInjection) {
        try {
            // 预加载CSS内容到缓存中
            await getCSSContent();
            if (config.debug) console.log('CSS预加载完成');
        } catch (error) {
            if (config.debug) console.warn('CSS预加载失败:', error);
        }
    }
})();


// 导出工具方法到全局命名空间（仅在打包为 IIFE 时有效）
try{
    window.V3Components = window.V3Components || {}
    Object.assign(window.V3Components, utils)
    // 直接挂载到 window 上，避免覆盖已有同名键
    Object.entries(utils).forEach(([key, fn])=>{
        if(!(key in window)){
            window[key]=fn
        }
    })
}catch(e){
    // 非浏览器环境忽略
}

export * from './utils.js'
